import { createFileRoute, Link, useMatchRoute, Outlet } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PageHero } from "@/components/site/Layout";
import { EventEmptyState } from "@/components/site/EventEmptyState";
import { fetchEventCollection } from "@/lib/api";
import { useSelectedEvent } from "@/lib/event-context";
import { eventLabel } from "@/lib/events";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

interface Sponsor {
  id: number;
  name: string;
  image_url: string | null;
}

export const Route = createFileRoute("/sponsors")({
  component: Sponsors,
  head: () => ({
    meta: [
      { title: "Sponsors — Arab Capital Markets Conference" },
      {
        name: "description",
        content: "Organizations partnering to deliver the Third Arab Capital Markets Conference.",
      },
      { property: "og:title", content: "Sponsors" },
      { property: "og:description", content: "Our hosts, organizers and sponsors." },
    ],
  }),
});

// ── Sponsorship Request Form ──────────────────────────────────────────────────

const sponsorshipSchema = z.object({
  full_name: z.string().min(1, "Full Name is required"),
  email: z.string().email("Invalid email address"),
  designation: z.string().optional(),
  company: z.string().optional(),
  phone_number: z.string().optional(),
  country: z.string().optional(),
  message: z.string().min(1, "Message is required"),
});

type SponsorshipValues = z.infer<typeof sponsorshipSchema>;

function SponsorshipForm() {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    reset,
  } = useForm<SponsorshipValues>({
    resolver: zodResolver(sponsorshipSchema),
  });

  const onSubmit = async (data: SponsorshipValues) => {
    try {
      const response = await fetch("/api/sponsorship-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (result.success) {
        toast.success("Thank you — we'll be in touch shortly.");
        reset();
      } else {
        toast.error(result.message || "Something went wrong. Please try again.");
      }
    } catch {
      toast.error("Network error — please try again.");
    }
  };

  return (
    <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8 border-t border-border/50">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 sm:mb-10 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent mb-3">
            Partner With Us
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-primary mb-3">
            Sponsorship Request
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto">
            Interested in sponsoring the conference? Fill in the form below and our team will get back to you.
          </p>
        </div>

        <form
          className="bg-card border border-border rounded-xl p-5 sm:p-6 md:p-8 space-y-4 shadow-sm"
          onSubmit={handleSubmit(onSubmit)}
        >
          {/* Row 1 – Full Name + Email */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">
                Full Name <span className="text-destructive">*</span>
              </Label>
              <Input id="full_name" {...register("full_name")} disabled={isSubmitting} />
              {errors.full_name && (
                <p className="text-xs text-destructive">{errors.full_name.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">
                Email Address <span className="text-destructive">*</span>
              </Label>
              <Input id="email" type="email" {...register("email")} disabled={isSubmitting} />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email.message}</p>
              )}
            </div>
          </div>

          {/* Row 2 – Designation + Company */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="designation">Designation</Label>
              <Input id="designation" {...register("designation")} disabled={isSubmitting} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">Company</Label>
              <Input id="company" {...register("company")} disabled={isSubmitting} />
            </div>
          </div>

          {/* Row 3 – Phone + Country */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone_number">Phone Number</Label>
              <Input id="phone_number" {...register("phone_number")} disabled={isSubmitting} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input id="country" {...register("country")} disabled={isSubmitting} />
            </div>
          </div>

          {/* Row 4 – Message */}
          <div className="space-y-2">
            <Label htmlFor="message">Your Message</Label>
            <Textarea id="message" rows={5} {...register("message")} disabled={isSubmitting} />
            {errors.message && (
              <p className="text-xs text-destructive">{errors.message.message}</p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full sm:w-auto bg-primary hover:bg-primary/90"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Sending..." : "Submit Request"}
          </Button>
        </form>
      </div>
    </section>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

function Sponsors() {
  const { selectedEvent, selectedEventId } = useSelectedEvent();
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);
  const matchRoute = useMatchRoute();

  useEffect(() => {
    if (!selectedEventId) {
      setSponsors([]);
      setLoading(false);
      return;
    }

    const loadSponsors = async () => {
      setLoading(true);
      try {
        const items = await fetchEventCollection<Sponsor>(
          `/api/events/${selectedEventId}/sponsors`,
          "/api/sponsors",
        );
        setSponsors(items);
      } catch (error) {
        console.error("Failed to load sponsors:", error);
        setSponsors([]);
      } finally {
        setLoading(false);
      }
    };

    loadSponsors();
  }, [selectedEventId]);

  // If we're on a child route (sponsor detail), render the Outlet
  const isChildRoute = matchRoute({ to: '/sponsors/$sponsorId' });
  if (isChildRoute) {
    return <Outlet />;
  }

  if (loading) {
    return (
      <>
        <PageHero
          
          title="Sponsors"
          subtitle="Made possible by the support of leading institutions"
        />
        <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center text-muted-foreground">Loading...</div>
        </section>
        <SponsorshipForm />
      </>
    );
  }

  return (
    <>
      <PageHero
      
        title="Sponsors"
        subtitle={selectedEvent ? eventLabel(selectedEvent) : "Made possible by the support of leading institutions"}
      />
      {!selectedEvent ? (
        <EventEmptyState title="Choose an event" message="Select an event in the header to see its sponsors." />
      ) : sponsors.length === 0 ? (
        <EventEmptyState title="No sponsors yet" message="Sponsors have not been assigned to this event." />
      ) : (
        <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-6">
              {sponsors.map((s) => (
                <Link
                  key={s.id}
                  to="/sponsors/$sponsorId"
                  params={{ sponsorId: s.id.toString() }}
                  className="group block"
                >
                  <Card
                    className="group overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-1 duration-300 p-4 sm:p-6 md:p-8 flex flex-col items-center justify-center min-h-[140px] sm:min-h-[160px] md:min-h-[180px] bg-card border-border/50 hover:border-accent/50 cursor-pointer"
                  >
                    {s.image_url ? (
                      <>
                        <div className="flex-1 flex items-center justify-center w-full">
                          <img
                            src={s.image_url}
                            alt={s.name}
                            className="w-full h-full max-h-[120px] sm:max-h-[140px] md:max-h-[160px] object-contain group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="mt-3 sm:mt-4 font-serif text-sm sm:text-base text-primary leading-tight text-center group-hover:text-accent transition-colors">
                          {s.name}
                        </div>
                      </>
                    ) : (
                      <div className="font-serif text-sm sm:text-base md:text-lg text-primary leading-tight text-center group-hover:text-accent transition-colors">
                        {s.name}
                      </div>
                    )}
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      <SponsorshipForm />
    </>
  );
}
